import {
  Component,
  ViewEncapsulation,
  OnInit,
  ChangeDetectorRef,
  OnDestroy,
} from '@angular/core';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { FormGroup, FormControl } from '@angular/forms';
import { WindowService } from 'src/shared/providers/window.service';
import { CustomDatePipe } from 'src/shared/pipes/custom-date.pipe';
import { ConfirmRequestPage } from '../confirm-request/confirm-request';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';
import { Util } from 'src/shared/util/util';
import { Booking } from 'src/providers/booking/booking';

@Component({
    selector: 'job-request-bids',
    templateUrl: 'job-request-bids.html',
    styleUrls: ['./job-request-bids.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class JobRequestBidsComponent implements OnInit, OnDestroy {
  isRightSideContent = true;
  dialogParams: any;
  errorMessage: string;
  quotes = [];
  topNavHeight = 0;
  loaded = false;
  selectedPrice: number;
  selectedDate: string;
  selectedTime: string;
  selectedHomekeeper: string;
  selectedHomekeeperName: string;
  selectedInstantBooking = true;
  selectedTimeObj: any; // Store selected time object for notes access
  dateLabel = 'Select Time';
  service: string;
  bookingData: any;
  form = new FormGroup({
    time: new FormControl(null),
  });
  private resizeObserver: ResizeObserver;

  boundConfirmBooking = this.confirmBooking.bind(this);

constructor(
    private navCtrl: CustomNavController,
    private rightSidePanelService: RightSidePanelService,
    private storage: TidyStorage,
    private changeDetectorRef: ChangeDetectorRef,
    public windowService: WindowService,
    private util: Util,
    private booking: Booking
  ) {}

  async ngOnInit() {
    try {
      this.dateLabel = (new TranslationPipe()).transform('Select Time');
      this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
      this.dialogParams = await this.storage.retrieve('dialog-params') || this.navCtrl.getParams();
      let potentialHomekeepers = [];
        try {
          const bookingId = this.dialogParams?.booking?.booking?.id;
          if (bookingId) {
            const response = await this.booking.getHomekeeperBids(bookingId);
            potentialHomekeepers = response || [];
            if (potentialHomekeepers.length === 0) {
              this.util.showError('No homekeeper bids found');
              if (this.isRightSideContent) {
                this.rightSidePanelService.goBackInLayers(1);
              } else {
                this.navCtrl.back();
              }
              return;
            }
          }
        } catch (error) {
          console.error('Error fetching homekeeper bids:', error);
          this.errorMessage = 'Error loading quotes. Please try again.';
          this.loaded = true;
          return;
        }


      this.quotes = this.getParsedQuotes(potentialHomekeepers);
      this.rightSidePanelService.setDialogPageTitle(
        (new TranslationPipe()).transform('Job Quotes')
      );
      this.service = this.dialogParams?.booking?.service_type_details?.name;
      this.bookingData = this.dialogParams?.booking;
      this.observeTopNavHeight();
      this.loaded = true;
    } catch (_error) {
      // Intentionally empty - errors handled elsewhere
    }
  }

  private observeTopNavHeight() {
    const element = document.getElementById('top-nav-height');
    if (element) {
      this.topNavHeight = element.offsetHeight;
      this.resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          this.topNavHeight = entry.contentRect.height;
          this.changeDetectorRef.detectChanges();
        }
      });
      this.resizeObserver.observe(element);
    }
  }

  ngOnDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  getParsedQuotes(potentialHomekeepers: any[]) {
    return potentialHomekeepers.map((quote, quoteIndex) => {
      let dates;
      let singlePrice: number | null = null;
      let singleNotes = '';

      // Handle new structure with rates array
      if (quote.rates && Array.isArray(quote.rates)) {
        dates = this.getDatesWithTimes(quote.rates, quoteIndex);
        // Aggregate price and notes from all time slots
        const allAmounts: number[] = [];

        // Find first note only (stop after finding one)
        for (const dateObj of dates) {
          for (const timeObj of dateObj.times) {
            if (timeObj.amount !== null && timeObj.amount !== undefined) {
              allAmounts.push(timeObj.amount);
            }
            // Get only the first note found
            if (!singleNotes && timeObj.homekeeper_notes && timeObj.homekeeper_notes.trim()) {
              singleNotes = timeObj.homekeeper_notes.trim();
            }
          }
        }

        // Use minimum price, or first available price
        singlePrice = allAmounts.length > 0 ? Math.min(...allAmounts) : null;
      } else {
        // Fallback for old structure (if still exists)
        dates = this.getDatesWithTimesFromLegacy(quote.times || [], quoteIndex);
        singlePrice = quote.amount || null;
        singleNotes = quote.homekeeper_notes || '';
      }

      const credentials = quote.credentials || [];
      const requiredCredentials = credentials
        .filter((c: any) => c.requirement === 'mandatory' || c.requirement === 'preferred')
        .map((c: any) => ({
          label: c.label,
          type: c.type,
          satisfied: c.requirement_satisfied === true,
        }));

      return {
        ...quote,
        name: `${quote.first_name} ${quote.last_name}`,
        dates,
        singlePrice,
        singleNotes,
        requiredCredentials,
        displayCredentials: this.getDisplayCredentials(credentials),
        profileImageUrl: this.getProfileImageUrl(credentials),
        pitchDescription: this.getPitchDescription(credentials),
      };
    });
  }

  getDatesWithTimesFromLegacy(times: any[], quoteIndex: number) {
    const dateMap = new Map<
      string,
      Set<{
        viewValue: string;
        value: string;
        time: string;
        amount: number | null;
        homekeeper_notes: string;
        instant_booking: boolean | null;
      }>
    >();

    times.forEach((time, index) => {
      const [date, timeRange] = time.split('T');
      const startTime = timeRange.split('-')[0].slice(0, 5);

      const [hour, minute] = startTime.split(':').map(Number);
      const period = hour >= 12 ? 'pm' : 'am';
      const hour12 = hour % 12 || 12;
      const formattedTime = `${hour12}:${minute
        .toString()
        .padStart(2, '0')}${period}`;

      if (!dateMap.has(date)) {
        dateMap.set(date, new Set());
      }
      dateMap.get(date)?.add({
        viewValue: formattedTime,
        value: `${startTime}-${quoteIndex}-${index}`,
        time: startTime,
        amount: null, // Legacy structure doesn't have per-time amounts
        homekeeper_notes: '',
        instant_booking: null,
      });
    });

    return Array.from(dateMap.entries()).map(([date, timesSet]) => ({
      date,
      times: Array.from(timesSet),
    }));
  }

  getDatesWithTimes(rates: any[], quoteIndex: number) {
    return rates.map((rate) => {
      const times = rate.times.map((timeObj: any, timeIndex: number) => {
        const timeStr = timeObj.time; // e.g., "08:00"
        const [hour, minute] = timeStr.split(':').map(Number);
        const period = hour >= 12 ? 'pm' : 'am';
        const hour12 = hour % 12 || 12;
        const formattedTime = `${hour12}:${minute.toString().padStart(2, '0')}${period}`;

        return {
          viewValue: formattedTime,
          value: `${timeStr}-${quoteIndex}-${rate.date}-${timeIndex}`,
          time: timeStr,
          amount: timeObj.amount,
          homekeeper_notes: timeObj.homekeeper_notes || '',
          instant_booking: timeObj.instant_booking,
          status: timeObj.status,
          timeObj, // Store full object for reference
        };
      });

      return {
        date: rate.date,
        times,
      };
    });
  }

  selectTime(event: any, quote: any, date: any) {
    // Find the selected time object from the date's times array
    const selectedTimeObj = date.times.find((t: any) => t.value === event.value);

    if (selectedTimeObj) {
      // Use time-level amount if available, otherwise fall back to quote-level amount (legacy)
      this.selectedPrice = selectedTimeObj.amount !== null && selectedTimeObj.amount !== undefined
        ? selectedTimeObj.amount
        : quote.amount;
      this.selectedDate = date.date;
      this.selectedTime = selectedTimeObj.time; // Store the actual time string
      this.selectedHomekeeper = quote.id;
      this.selectedHomekeeperName = quote.name;
      // Use time-level instant_booking if available, otherwise fall back to quote-level (legacy)
      this.selectedInstantBooking = selectedTimeObj.instant_booking !== null && selectedTimeObj.instant_booking !== undefined
        ? selectedTimeObj.instant_booking
        : quote.instant_booking;
      this.selectedTimeObj = selectedTimeObj; // Store full time object for notes access

      this.dateLabel =
        `${quote.name
        } - ${
        new CustomDatePipe().transform(date.date, 'dddd MMM D', '')
        } ${
        event.viewValue}`;
    }
  }

  confirmBooking() {
    const jobId = this.bookingData?.booking?.current_job?.id;
    const params = {
      ...this.dialogParams,
      viewData: {
        billingType: this.bookingData?.service_type_details?.billing_type,
        duration: this.dateLabel,
        frequency: this.bookingData?.service_type_details?.key,
        image: this.dialogParams.icon,
        price: this.selectedPrice,
        service: this.service,
        homekeeper_id: this.selectedHomekeeper,
        homekeeper_name: this.selectedHomekeeperName,
        date: this.selectedDate,
        time: this.selectedTime,
        booking_id: this.bookingData?.booking?.id,
        instant_booking: this.selectedInstantBooking,
        job_id: jobId,
      },
      customBack: 'job-request-bids',
    };
    if (this.isRightSideContent) {
      this.rightSidePanelService.navigateTo(
        'confirm-request',
        params,
        ConfirmRequestPage
      );
    } else {
      this.navCtrl.navigateForward('confirm-request', params);
    }
  }

  shouldShowTimeSlotDetails(times: any[]): boolean {
    if (!times || times.length === 0) {
      return false;
    }
    // Show details if there are multiple times (to show different amounts) or if any time has notes
    return times.length > 1 || times.some(t => t.homekeeper_notes && t.homekeeper_notes.trim());
  }

  openWebsite(url: string) {
    this.util.openUrl(url);
  }

  getDisplayCredentials(credentials: any[]): any[] {
    if (!credentials) {return [];}
    return credentials.filter(c => !c.requirement && c.category !== 'core_profile');
  }

  getProfileImageUrl(credentials: any[]): string | null {
    const cred = credentials?.find(c => c.category === 'core_profile' && c.file_url && this.isImageUrl(c.file_url));
    return cred?.file_url || null;
  }

  getPitchDescription(credentials: any[]): string | null {
    const cred = credentials?.find(c => c.category === 'core_profile' && c.text_value);
    return cred?.text_value || null;
  }

  private isImageUrl(url: string): boolean {
    if (!url) {return false;}
    return /\.(jpg|jpeg|png|gif|webp|svg)(\?|$)/i.test(url) || url.includes('/homekeeper-credentials/');
  }

}
